<script setup lang="ts">
import { onMounted, readonly } from 'vue';
import { useForm } from 'vee-validate';
import { yup } from '@clearbanc/validation';

import statesByCountry from '@/data/states-by-country';
import { COUNTRY_CODES } from '@/data/supported-country-codes';
import {
  federalTaxIdLabel,
  federalTaxIdShortLabel,
  stateLabel,
  stateOptionsForCountry,
} from '@/utils/local';
import { qualifiedNorthAmericanCountries } from '@/data/country-code-with-names';
import { BusinessDetails } from '@/types/business.types';
import { corpTypesForCountry } from '@/data/corp-types-by-country';
import {
  CountryCodeEntryWithPhone,
  getAllCallingCodes,
  getPhoneNumberCountryCode,
  getPhoneNumberWithoutCountryCode,
} from '@/utils/phone-numbers';

import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import DropdownGroup from '@/components/forms/groups/DropdownGroup.vue';
import FormPhoneInput from '@/components/forms/FormPhoneInput.vue';
import BusinessDetailsFormInput from './BusinessDetailsFormInput.vue';

interface BusinessInfoForm extends BusinessDetails {
  countryCallingCode?: CountryCodeEntryWithPhone;
}

const props = defineProps<{ business: BusinessDetails }>();

// ---------
// Variables
// ---------

const callingCodes = getAllCallingCodes();

const countries = readonly(
  qualifiedNorthAmericanCountries().map((country) => ({
    value: country.value,
    nameString: country.nameString,
  })),
);

const states = (country: string) => {
  return statesByCountry[country] || [];
};

// ----------
// Form Logic
// ----------

const validationSchema = yup.object({
  corpCountry: yup.string(),
  corpState: (yup.string() as any).requiredWhen(
    'corpCountry',
    (country: string) => stateOptionsForCountry(country),
  ),
  name: yup.businessName(),
  phone: yup.phone('countryCallingCode'),
  countryCallingCode: yup.phoneCode(),
  federalTaxId: yup.ein('corpCountry'),
  corpType: yup.string().required('Required'),
  // TODO SC-192429 - We can revert this after updating typescript definitions
  // in the @clearbanc/validation to include these missing methods
  corpTypeOther: (yup.string() as any).requiredWhen(
    'corpType',
    (corpType: string) => corpType === 'other_option',
  ),
});

const { values, setValues, validate } = useForm<BusinessInfoForm>({
  validationSchema,
});

async function validateAndGetValues() {
  const { valid } = await validate();
  if (valid) {
    const phone =
      `${values.countryCallingCode?.callingCode}${values.phone}`.replace(
        /\s+/g,
        '',
      );
    return {
      corpCountry: values.corpCountry,
      corpState: values.corpState,
      name: values.name,
      phone,
      federalTaxId: values.federalTaxId,
      corpType: values.corpTypeOther
        ? `other - ${values.corpTypeOther}`
        : values.corpType,
    };
  }
  return undefined;
}

defineExpose({
  validateAndGetValues,
});

function populateSavedValues() {
  const {
    corpCountry,
    corpState,
    name,
    phone,
    federalTaxId,
    corpType,
    corpTypeOther,
  } = props.business;

  const usCountryCode = callingCodes.find((item) => {
    return item.value === COUNTRY_CODES.US;
  });
  const countryCallingCode = phone
    ? getPhoneNumberCountryCode(phone)
    : usCountryCode;

  setValues({
    ...(corpCountry && { corpCountry }),
    ...(corpState && { corpState }),
    name,
    countryCallingCode: countryCallingCode ?? usCountryCode,
    ...(phone && { phone: getPhoneNumberWithoutCountryCode(phone) }),
    ...(federalTaxId && { federalTaxId }),
    ...(corpType && { corpType }),
    ...(corpTypeOther && { corpTypeOther }),
  });
}

onMounted(() => {
  populateSavedValues();
});
</script>

<template>
  <div class="c-group">
    <div class="c-group__header">
      <h3 class="c-group__header__title">Business Information</h3>
    </div>
    <div class="c-form__row grid grid-cols-12">
      <div class="col-span-6 md:col-span-12">
        <DropdownGroup
          name="corpCountry"
          label="Country"
          :options="countries"
          option-label="nameString"
          option-value="value"
          placeholder="Select"
          append-to="self"
          :reset-filter-on-hide="true"
          @change="
            setValues({
              ...values,
              corpState: undefined,
            })
          "
        />
      </div>
      <div class="col-span-6 md:col-span-12">
        <DropdownGroup
          name="corpState"
          :label="stateLabel(values.corpCountry)"
          :options="states(values.corpCountry)"
          option-label="nameString"
          option-value="value"
          placeholder="Select"
          append-to="self"
          :filter="true"
          :reset-filter-on-hide="true"
          :disabled="!values.corpCountry"
        />
      </div>
    </div>
    <div class="c-form__row grid grid-cols-12">
      <div class="col-span-6 md:col-span-12">
        <InputTextGroup name="name" label="Registered Business Name" />
        <BusinessDetailsFormInput>
          Include structure (i.e. ltd., inc., etc)
        </BusinessDetailsFormInput>
      </div>
      <div class="col-span-6 md:col-span-12">
        <FormPhoneInput
          :dropdown-attrs="{
            name: 'countryCallingCode',
            options: callingCodes,
            optionLabel: 'nameString',
            placeholder: 'Select',
            filter: true,
          }"
          :input-attrs="{
            name: 'phone',
            label: 'Business Phone Number',
          }"
        />
      </div>
    </div>
    <div class="c-form__row grid grid-cols-12">
      <div class="col-span-6 md:col-span-12">
        <InputTextGroup
          v-only-numbers
          name="federalTaxId"
          :label="
            federalTaxIdLabel(values.corpCountry) ||
            federalTaxIdShortLabel(values.corpCountry) ||
            federalTaxIdLabel(COUNTRY_CODES.US)
          "
          :disabled="!values.corpCountry"
        />
      </div>
      <div class="col-span-6 md:col-span-12">
        <DropdownGroup
          name="corpType"
          label="Legal Business Structure"
          :options="corpTypesForCountry(values.corpCountry)"
          option-label="nameString"
          option-value="value"
          placeholder="Select"
          append-to="self"
          :reset-filter-on-hide="true"
          :disabled="!values.corpCountry"
        />
      </div>
    </div>
    <div class="c-form__row grid grid-cols-12">
      <div
        v-if="(values.corpType as string) === 'other_option'"
        class="col-span-6 md:col-span-12"
      >
        <InputTextGroup
          name="corpTypeOther"
          label="Other Legal Business Structure"
        />
      </div>
    </div>
  </div>
</template>
